import { joinLabels, pageLabel, positive, readingTime } from '../armKit';
import { deepWorkFacts } from '../pageData';
import { cardPartCss, chipsRowCss, MUTED, snapshotListOrder } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ListSnapshot, ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

// Module scope, not the toggle's closure: the snapshot is captured before the
// FIRST sort and kept across reverts, so the canonical original order is the
// author's, never whatever a later restore reassembled — and onCards must
// reach it to note every appended batch before a re-sort scrambles it.
let snapshot: ListSnapshot | null = null;

const FLAG = 'readingscent';
// 'Downloadable' rather than the roster's 'PDF': works_json exposes no
// per-file type, so PDF would be a guess on every docx-only corpus.
const FULL_TEXT = 'Downloadable';
const SORT_CLASS = 'profile-liautotest-scent-sort';
const SORT_LABEL = 'Sort by reading time';
const SORT_SOURCE = 'profile-liautotest-scent-sort';
// The revert tracks under its own source rather than a second click on the
// same one: the metric reads intent, and on-counts and off-counts have to be
// separable downstream where source is the only key.
const SORT_OFF_SOURCE = `${SORT_SOURCE}-off`;

function armCss(scope: string): string {
  return (
    chipsRowCss(scope, { pill: false, lineHeight: '20px' }) +
    // 14px over the chips base sheet's 12px. The base puts this row's
    // typography on the row, not the span, and chipsRowCss carries no
    // font-size knob — a single-arm opinion, so it rides in cardPartCss.
    cardPartCss('chips', scope, 'font-size: 14px;') +
    // A pill dressed exactly as the section chips above it — same resting
    // greys, same blue active state (.nav-chip.active: #e6ecf7 fill, #b4c7e8
    // rule, #0c3b8d text), same hover — so pressed reads as "selected" in the
    // page's own vocabulary. The hexes are the chips' literals from
    // wow/_profile.scss; no token carries them. aria-pressed keys the state
    // for assistive tech and the sheet alike.
    `
${scope} .${SORT_CLASS} {
  display: inline-block;
  margin: 0 0 10px;
  appearance: none;
  height: 28px;
  padding: 0 16px;
  border: 1px solid #dddde2;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  line-height: 26px;
  color: ${MUTED};
  background: #fff;
  cursor: pointer;
}
${scope} .${SORT_CLASS}:not([aria-pressed='true']):hover {
  background: #e6ecf7;
  color: #0c3b8d;
}
${scope} .${SORT_CLASS}[aria-pressed='true'] {
  background: #e6ecf7;
  border-color: #b4c7e8;
  color: #0c3b8d;
}
`
  );
}

function lineFor(card: ProfileCard, facts: Facts): string | null {
  const pages = positive(facts.get(card.workId)?.pageCount);
  if (pages == null) return null;

  const time = readingTime(pages);

  return joinLabels([
    card.hasDownloadable ? FULL_TEXT : null,
    pageLabel(pages),
    time == null ? null : `${time} read`,
  ]);
}

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

function chipEach(cards: ProfileCard[], facts: Facts): void {
  cards.forEach((card) => {
    if (marked(card)) return;

    const line = lineFor(card, facts);
    if (line == null) return;

    card.setFlag(FLAG);
    card.addChip(line);
  });
}

// Not bySubstance — that order is file-first. This one is length alone: page
// count descending, unknown lengths sinking below known ones rather than
// sorting as zero (0 is how the endpoint spells "never counted", the same
// reading pageLabel gives it, so the sort and the line cannot disagree).
function byReadingTime(
  facts: Facts,
): (a: ProfileCard, b: ProfileCard) => number {
  return (a, b) => {
    const left = positive(facts.get(a.workId)?.pageCount);
    const right = positive(facts.get(b.workId)?.pageCount);
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    return right - left;
  };
}

function sortControl(ctx: ArmContext, facts: Facts): HTMLButtonElement {
  const button = document.createElement('button');
  // The default type is submit, and this control can land inside a page form.
  button.type = 'button';
  button.className = SORT_CLASS;
  button.textContent = SORT_LABEL;
  button.setAttribute('aria-pressed', 'false');

  // Sorted-or-not lives on the DOM as aria-pressed — the same attribute
  // onCards reads back and the pressed pill styles on.
  button.addEventListener('click', () => {
    if (button.getAttribute('aria-pressed') === 'true') {
      ctx.track(SORT_OFF_SOURCE);
      snapshot?.restore();
      button.setAttribute('aria-pressed', 'false');
      return;
    }

    snapshot ??= snapshotListOrder();
    ctx.track(SORT_SOURCE);
    ctx.reorderCards(byReadingTime(facts), 'list');
    button.setAttribute('aria-pressed', 'true');
  });

  return button;
}

function sorted(): boolean {
  return document.querySelector(`.${SORT_CLASS}[aria-pressed="true"]`) != null;
}

export const readingScent: ArmSpec = {
  // stamp deliberately unset: the gate is deepWorkFacts' resolved map, and a
  // fetch-gated arm cannot predict render_state — markRendered is the record.
  onReady: async (ctx) => {
    // Module state outlives a pageview: every setup starts with no snapshot.
    snapshot = null;

    const facts = await deepWorkFacts(ctx.viewedUserId);
    if (facts.size === 0) return;

    const cards = ctx.cards();
    // No known page count on any rendered card is a control render too: the
    // line is the treatment, and a sort control over zero known lengths would
    // be a visible mutation markRendered never vouched for.
    if (!cards.some((card) => lineFor(card, facts) != null)) return;

    ctx.injectStyles(armCss(ctx.scope));
    chipEach(cards, facts);
    ctx.markRendered();

    // listStart, not columnTop: tucked at the column's top right the control
    // read as a stray line, and directly above the works list it reads as a
    // sort option for the list it acts on. Mounted after markRendered because
    // the chips are the treatment — the control is furniture on top of them.
    ctx.mountSlot('listStart')?.appendChild(sortControl(ctx, facts));
  },

  // A batch is chipped first and only then re-sorted, so it never lands
  // unlabelled above the fold. Re-sorting moves cards under a reader who is
  // already scrolling — accepted here, and only here, because the user opted
  // into this order by clicking; unsorted, a batch keeps its arrival order.
  // Gated on the marks onReady left, so a late batch cannot engage an arm
  // that degraded to control.
  onCards: async (cards, ctx) => {
    if (!ctx.cards().some(marked)) return;

    const facts = await deepWorkFacts(ctx.viewedUserId);
    chipEach(cards, facts);

    // Noted BEFORE the re-sort, while the batch still sits in arrival order —
    // that is the order restore replays; noted after, it would replay the
    // reading-time order the re-sort left behind.
    snapshot?.note();
    if (sorted()) ctx.reorderCards(byReadingTime(facts), 'list');
  },
};
